import { useCallback, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { toDisplayString } from "../../utils/normalizeApiError";
import { shouldUseSuccessScreen, showSuccessScreen } from "../common/successScreenBus";
import { MESSAGE_DURATION_MS, useSecondsLeft } from "../../hooks/useAutoHide";
import MessageCountdown from "../common/MessageCountdown";
import "./successCelebration.css";

export function useToastStack() {
  const [toasts, setToasts] = useState([]);
  const counter = useRef(0);

  const addToast = useCallback((message, type = "success", options = {}) => {
    // Settings saves use the shared success screen; errors / warnings stay toasts.
    if (type === "success") {
      const text = toDisplayString(message, "");
      if (text && shouldUseSuccessScreen(text, options)) {
        showSuccessScreen(text);
        return;
      }
    }
    const id = ++counter.current;
    const title =
      type === "success"
        ? options.title || "Successfully completed"
        : options.title || "";
    const duration = options.duration ?? MESSAGE_DURATION_MS;
    setToasts((prev) => [...prev, { id, message, type, title, endAt: Date.now() + duration }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return { toasts, addToast, removeToast };
}

function ToastCountdown({ endAt, onClose }) {
  const seconds = useSecondsLeft(endAt);
  return <MessageCountdown seconds={seconds} onClose={onClose} className="-my-1 -mr-1" />;
}

export function ToastStack({ toasts, onRemove }) {
  if (!toasts?.length) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-16 z-[250] flex flex-col items-stretch gap-2 px-4 sm:inset-x-auto sm:right-4 sm:top-20 sm:items-end"
      aria-live="polite"
    >
      {toasts.map((t) => {
        const isSuccess = t.type === "success";
        return (
          <div
            key={t.id}
            className={[
              "pointer-events-auto flex max-w-md items-start gap-2 rounded-lg border px-3 py-2.5 text-sm shadow-lg transition duration-200 ease-out",
              isSuccess
                ? "brix-toast-success"
                : "border-l-4 border-l-amber-500 border-amber-200 bg-amber-50 text-amber-900",
            ].join(" ")}
          >
            <span className="mt-0.5 shrink-0">
              {isSuccess ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-hidden />
              ) : (
                <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden />
              )}
            </span>
            <div className="min-w-0 flex-1 leading-snug break-words">
              {isSuccess && t.title ? (
                <div className="brix-toast-success-title">{toDisplayString(t.title)}</div>
              ) : null}
              <p className="m-0 font-semibold">
                {toDisplayString(t.message, isSuccess ? "Updated successfully." : "Something went wrong.")}
              </p>
            </div>
            <ToastCountdown endAt={t.endAt} onClose={() => onRemove(t.id)} />
          </div>
        );
      })}
    </div>
  );
}
